import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { T } from "../mock";

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("lelune_lang") || "nl");
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("lelune_cart")) || [];
    } catch {
      return [];
    }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistProductId, setWaitlistProductId] = useState("copper-peptide-serum-30ml");

  const openWaitlist = (productId = "copper-peptide-serum-30ml") => {
    setWaitlistProductId(productId);
    setWaitlistOpen(true);
  };

  useEffect(() => {
    localStorage.setItem("lelune_lang", lang);
  }, [lang]);

  useEffect(() => {
    localStorage.setItem("lelune_cart", JSON.stringify(cart));
  }, [cart]);

  const t = useMemo(() => T[lang], [lang]);
  const toggleLang = () => setLang((l) => (l === "nl" ? "en" : "nl"));

  const lineKey = (id, shade) => `${id}__${shade || "default"}`;

  const addToCart = (product, shade = null, qty = 1) => {
    setCart((prev) => {
      const key = lineKey(product.id, shade?.code);
      const existing = prev.find((l) => l.key === key);
      if (existing) {
        return prev.map((l) => (l.key === key ? { ...l, qty: l.qty + qty } : l));
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          shade: shade ? shade.code : null,
          shadeName: shade ? shade.name : null,
          preorder: product.preorder,
          qty,
        },
      ];
    });
    setCartOpen(true);
  };

  const removeLine = (key) => setCart((prev) => prev.filter((l) => l.key !== key));
  const clearCart = () => setCart([]);
  const updateQty = (key, qty) =>
    setCart((prev) =>
      prev
        .map((l) => (l.key === key ? { ...l, qty: Math.max(1, qty) } : l))
        .filter((l) => l.qty > 0)
    );

  const cartCount = cart.reduce((s, l) => s + l.qty, 0);
  const subtotal = cart.reduce((s, l) => s + l.qty * l.price, 0);

  const value = {
    lang,
    setLang,
    toggleLang,
    t,
    cart,
    addToCart,
    removeLine,
    clearCart,
    updateQty,
    cartOpen,
    setCartOpen,
    waitlistOpen,
    setWaitlistOpen,
    waitlistProductId,
    openWaitlist,
    cartCount,
    subtotal,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
